/**
 * The Pomiary tab — KANON `pomiary-klienta-2026-09-18`, direction D "Dwie daty".
 *
 * Two cards. The STAGE carries the current reading of the chosen quantity, one sentence about the
 * stretch between A and B, the presets, the quantity switch, the quiet „Pomiar" button, a 400px
 * plot with two draggable markers, and the Trend and Prognoza toggles. The TABLE under it says what
 * changed in all eleven quantities, as bars diverging from a centred zero on one shared scale.
 *
 * Every decision here is `model/twoDates`'. This file places boxes and binds gestures.
 *
 * ── The quantity switch offers all ELEVEN, and it is ONE segmented pill ──────────────────────
 * Owner, 19 September 2026: one segmented pill, never Trendy's `.scaleOption` — eleven separately
 * bordered chips wrapping onto two rows is a different control wearing the same words. Owner,
 * 25 September 2026: the pill offers every quantity the table prints, not six of them. Where the
 * card cannot hold eleven options on one row, the pill scrolls sideways instead of wrapping.
 *
 * ── Fewer than two measurements ──────────────────────────────────────────────────────────────
 * With none at all the plot's box holds `EmptyState` in tone `action` — the drawer that takes the
 * first one is on this very tab, and the sentence slot above it stays BLANK rather than repeating
 * the same words in a quieter voice. With ONE, the chart draws its single reading, the markers are
 * not rendered (two dates cannot be named), the sentence is replaced by "Potrzeba dwóch pomiarów…"
 * and every table row reads „bez pary" with no bar. Nothing is disabled and nothing disappears: the
 * two cards keep the same boxes, which is what stops the tab moving as a client's history grows.
 *
 * ── FAILED is a state of its own, in all three boxes ─────────────────────────────────────────
 * `pending`, `failed` and `empty` are three answers and never a branch on the data. A failed read
 * used to reach the stage sentence and the comparison table as "this client has no measurements"
 * and eleven „bez pary" rows — assertions about a client nobody had read. Each of the three boxes
 * now names the failure inside the reserve it already held, and the retry lives on the plot's own
 * `EmptyState`, which is where this slice puts it everywhere else.
 *
 * ── The tab's answer is SPOKEN ───────────────────────────────────────────────────────────────
 * One polite live region for the whole tab, publishing `view.sentence.spoken` — which the model
 * builds, so the words a screen reader hears cannot drift from the words on the card. Because every
 * move SNAPS to a measurement, a pointer drag changes that string only when the snapped pair
 * changes: dragging across half a plot announces once per measurement crossed, never per frame.
 */

import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { motion } from 'motion/react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { screenMotion } from '~/shared/design-system/motion';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ReadoutChart } from '~/shared/ui/chart/ReadoutChart';
import { EmptyState } from '~/shared/ui/EmptyState';
import { MeasureMarkIcon } from '~/shared/ui/EmptyStateIcons';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useMeasurements } from '../lib/useMeasurements';
import {
  MEASUREMENT_CHART_PARAMETERS,
  MEASUREMENT_PARAMETERS,
  measurementShortLabelKey,
  measurementUnitKey,
  type MeasurementParameter,
} from '../model/measurements';
import type { MarkerKey } from '../model/twoDates';

import { useCardAnnounce } from './cardAnnouncer';
import styles from './DetailTab.module.css';
import { usePublishTabMeta } from './detailTabMeta';
import { MeasurementDrawer } from './MeasurementDrawer';
import {
  toggleAid,
  twoDatesPlot,
  TwoDatesPlotLayers,
  TwoDatesSayWait,
  TwoDatesSentenceText,
  TwoDatesToggles,
} from './TwoDatesStageParts';
import { TwoDatesTable } from './TwoDatesTable';

const KEY = 'web:clientDetail.body';

/** A trainer's tape session, solid. */
const FULL_DOT = 3.2;
/** A client's own check-in: smaller and hollow. */
const CHECKIN_DOT = 2.2;

interface BodyTabProps {
  readonly clientId: number;
  readonly reducedMotion: boolean;
}

export function BodyTab({ clientId, reducedMotion }: BodyTabProps) {
  const { t } = useTranslation();
  const [parameter, setParameter] = useState<MeasurementParameter>('weightKg');
  // The canon's defaults: the trend is what a trainer wants to read, the forecast is a question
  // they ask on purpose.
  const [trend, setTrend] = useState(true);
  const [projection, setProjection] = useState(false);
  const [adding, setAdding] = useState(false);

  const body = useMeasurements(clientId, { parameter, trend, projection });
  const view = body.view;
  // One gate for the tab: the stage and the table come off the same two reads.
  const gate = useSkeletonGate(body.pending);

  // The tab bar's line is part of the same answer, so it lands with it: published on the read it
  // would appear in the bar while this tab is still drawing bars.
  usePublishTabMeta(
    gate.waiting || view.latestLabel === null
      ? null
      : t('web:clientDetail.tabMeta.body', { date: view.latestLabel }),
  );

  // The caption belongs to the ANSWER, so it waits with the answer rather than with the read:
  // printed the moment `/measurements` lands it would be new text arriving while the bars are
  // still on screen, which is a change inside the wait rather than a hand-over out of it.
  const short = gate.waiting || body.failed || view.empty || view.noSeries;
  const nameOf = (marker: MarkerKey) => t(`${KEY}.marker.${marker}`);

  /**
   * Say the answer, once it has settled.
   *
   * The dependency is the SENTENCE rather than the selection, because a move that lands on the same
   * pair — an arrow at the end of the history, a preset that already described it — is not a new
   * answer and must not be spoken as one. `announce` is stable by contract, so this effect runs on
   * a change of words and on nothing else.
   */
  const announce = useCardAnnounce();
  const spoken = view.sentence?.spoken ?? '';
  useEffect(() => {
    if (spoken !== '') announce(spoken);
  }, [announce, spoken]);

  const ids = useId();
  const presetReasonId = `${ids}-preset-reason`;
  /**
   * A REFUSAL IS AN ANSWER, so it waits for one.
   *
   * With no snapshots in hand every preset and both toggles are unanswerable — and that is as true
   * while the read is open as when it comes back empty, which is the conflation this whole tab is
   * written against. Drawn during the wait it also MOVED the screen: reason captions stood in the
   * presets column and vanished when the response landed, measured at **24px** of single-frame
   * movement on the plot, the table card and its first row at 1200ms of latency.
   *
   * It waits on the GATE rather than on the read, for that same measurement: with `body.pending`
   * the captions appeared the instant the response arrived, while the bars were still serving
   * their minimum life, so the 24px would have moved DURING the wait instead of out of it.
   */
  const answered = !gate.waiting && !body.failed;
  /**
   * The one reason showing, if any. `model/twoDates` guarantees at most one distinct reason across
   * the four presets, which is what lets the caption reserve a single line and never move.
   */
  const presetReason =
    (answered
      ? view.presets.find((preset) => preset.unavailableReason !== null)?.unavailableReason
      : null) ?? '';

  return (
    <div className={styles.tab}>
      <motion.section
        className={`${String(styles.card)} ${String(styles.stageCard)}`}
        {...screenMotion('enter', { reducedMotion })}
      >
        <div className={styles.stageHead}>
          {/* The reading and the sentence stand in a box that already knows its height, so the
            * plot under them does not travel when the read lands. */}
          <div className={styles.stageSay}>
            {gate.waiting ? (
              <TwoDatesSayWait skeleton={gate.skeleton} />
            ) : (
              <>
                {view.current === null ? null : (
                  <p className={`${String(styles.nowLine)} ${gate.reveal}`}>
                    <span className={styles.nowLabel}>{view.seriesLabel}</span>
                    <b className={styles.nowValue}>{view.current.value}</b>
                    <span className={styles.nowDate}>{view.current.dateLabel}</span>
                  </p>
                )}
                {/* FAILED and EMPTY both leave the reserve standing and take only the ink: the
                  * plot's own box under this one already carries the whole message, and the same
                  * sentence twice in one card is the card answering once and repeating itself.
                  * NO SERIES speaks, because the plot's neutral state says which quantity is
                  * missing and the sentence would otherwise go on naming an A and a B for a series
                  * with nothing in it. */}
                {body.failed || view.empty ? null : view.sentence === null ? (
                  <p className={styles.sentenceQuiet}>
                    {view.noSeries ? t(`${KEY}.noSeries`) : t(`${KEY}.needsTwo`)}
                  </p>
                ) : (
                  <TwoDatesSentenceText className={gate.reveal} sentence={view.sentence} />
                )}
              </>
            )}
          </div>

          <div className={styles.stageControls}>
            <div className={styles.presetsBlock}>
              <div className={styles.presets} role="group" aria-label={t(`${KEY}.presetsLabel`)}>
                {view.presets.map((preset) => (
                  <button
                    // A preset the history cannot answer stays FOCUSABLE and carries its own reason
                    // as a VISIBLE caption below the row, rather than dropping out of the tab order
                    // with an explanation only a mouse could reach. `title` was that explanation,
                    // and it was one sentence for four different refusals.
                    aria-describedby={
                      answered && preset.unavailableReason !== null ? presetReasonId : undefined
                    }
                    aria-disabled={answered && !preset.available ? true : undefined}
                    aria-pressed={preset.active}
                    className={`${String(styles.preset)} ${preset.active ? String(styles.presetOn) : ''} ${answered && !preset.available ? String(styles.presetOff) : ''}`}
                    key={preset.key}
                    onClick={() => {
                      if (preset.available) body.move({ kind: 'preset', preset: preset.key });
                    }}
                    type="button"
                  >
                    <i aria-hidden="true" className={styles.presetDot} />
                    {preset.label}
                  </button>
                ))}
              </div>
              {/* ALWAYS DRAWN, and empty when nothing is refused. The caption's line is part of the
                * box from the first frame, so a refusal appearing with the answer changes ink and
                * never geometry — measured at 24px of movement on the plot, the table and its first
                * row when this was conditional. At most ONE reason can be showing (see
                * `model/twoDates`' `shortHistory`), which is what makes one line enough. */}
              <em className={styles.presetReason} id={presetReasonId}>
                {presetReason}
              </em>
            </div>

            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>{t(`${KEY}.lineLabel`)}</span>
              {/* The canon's ONE segmented pill, on one row: a padded chip track with the chosen
                * option lifted onto the card's own fill. It is deliberately not Trendy's
                * `.scaleOption`, which is a row of separately bordered chips — that control was
                * reused here at eleven options, wrapped onto two rows, and read as eleven chips
                * rather than as one switch. All eleven quantities sit in this one pill. */}
              <RadioGroup
                aria-label={t(`${KEY}.lineSwitch`)}
                className={styles.seg}
                onValueChange={(next) => {
                  const chosen = MEASUREMENT_CHART_PARAMETERS.find((option) => option === next);
                  if (chosen !== undefined) setParameter(chosen);
                }}
                value={parameter}
              >
                {MEASUREMENT_CHART_PARAMETERS.map((option) => (
                  <Radio.Root className={styles.segOption} key={option} value={option}>
                    {t(measurementShortLabelKey(option))}
                  </Radio.Root>
                ))}
              </RadioGroup>
              <MeasurementDrawer
                body={body}
                onOpenChange={setAdding}
                open={adding}
                triggerClassName={String(styles.measureButton)}
              />
            </div>
          </div>
        </div>

        {/* pending, error and empty are three states in ONE reserved box — the plot's own. */}
        {body.failed ? (
          <div className={styles.plotReserve}>
            {/* The retry lives HERE and only here — the slice's own pattern, and the plot is the
              * box the trainer is looking at when the tab cannot answer. */}
            <EmptyState
              mark={<MeasureMarkIcon />}
              title={
                <span>
                  {t(`${KEY}.failed`)}{' '}
                  <button
                    className={inlineButton.inlineTextButton}
                    onClick={body.retry}
                    type="button"
                  >
                    {t('web:clientDetail.states.retry')}
                  </button>
                </span>
              }
              tone="neutral"
            />
          </div>
        ) : gate.waiting ? (
          // `aria-busy` is the WAIT's (owner, 21 September 2026): while the bars stand, the box is
          // still about to change, and calling it settled the moment the reads answered would be a
          // settlement claimed before it happened. A literal, because this box is only mounted
          // inside the wait branch: `gate.waiting` is true for exactly as long as it exists.
          <div aria-busy="true" className={styles.plotReserve}>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {/* A plot nobody has drawn is a flat band across the box it will be drawn in. */}
            {gate.skeleton ? (
              <i className={`${String(skel.bar)} ${String(styles.skelPlot)}`} />
            ) : null}
          </div>
        ) : view.readings.length === 0 ? (
          <div className={styles.plotReserve}>
            {/* ACTION for a client with no measurements at all — the drawer that takes the first
              * one is on this tab. NEUTRAL for a quantity this client has simply never had taped. */}
            <EmptyState
              mark={<MeasureMarkIcon />}
              title={view.empty ? t(`${KEY}.empty`) : t(`${KEY}.noSeries`)}
              tone={view.empty ? 'action' : 'neutral'}
              {...(view.empty ? { hint: t(`${KEY}.emptyHint`) } : {})}
            />
          </div>
        ) : (
          <ReadoutChart
            className={gate.reveal}
            colour="var(--chart-series-neutral)"
            height="var(--h-chart-plot-two-dates)"
            name={view.seriesLabel}
            // The readout and the hidden data table carry the unit; the y gutter does not, because
            // `yTickStyle: 'bare'` prints the figure alone. The canon spells it the same way.
            unit={t(measurementUnitKey(parameter))}
            points={view.readings}
            reducedMotion={reducedMotion}
            // A body measurement has no meaningful zero: a waist plotted from 0 cm is a flat line
            // at the top of the box.
            zeroBaseline={false}
            extraYValues={view.extraY}
            // With Trend on the LINE is the moving average and bklit draws the raw readings as the
            // dots under it. The readout, the keyboard path and the hidden table still read the raw
            // readings either way, so nothing a trainer can read off the plot is ever smoothed.
            seriesAs={view.trend === null ? 'line' : 'dots'}
            // The canon's own plot, member by member: 30px of top margin for the grips that stand
            // clear of the box, a right gutter that widens for the forecast's end label, month
            // names on the x axis, a bare figure on the y axis, NO neutral wash (the only wash on
            // this plot is the ember one inside A..B), and a mark that says who took the reading.
            plot={{
              ...twoDatesPlot(view.projection !== null),
              // WHO recorded a reading is told by the mark only when the line IS the readings —
              // Trend off. With Trend on the line is a moving average and the raw readings are the
              // scatter it was drawn through, which the canon draws as one quiet grey dot each:
              // two things said at once there would make the loudest mark on the plot a fact about
              // paperwork rather than about the body.
              ...(view.trend === null
                ? {
                    markAt: (at: Date) => {
                      const fact = view.readingFacts.get(at.getTime());
                      if (fact === undefined) return null;
                      return fact.byTrainer
                        ? { radius: FULL_DOT, filled: true }
                        : { radius: CHECKIN_DOT, filled: false };
                    },
                  }
                : {}),
              notesAt: (at) => view.readingFacts.get(at.getTime())?.notes ?? [],
            }}
            {...(view.xDomain === null ? {} : { xDomain: view.xDomain })}
          >
            <TwoDatesPlotLayers
              nameOf={nameOf}
              onMove={body.move}
              reducedMotion={reducedMotion}
              view={view}
            />
          </ReadoutChart>
        )}

        <TwoDatesToggles
          answered={answered}
          instruction={t(`${KEY}.instruction`)}
          refusals={{
            trend: t(`${KEY}.trendUnavailable`),
            projection: t(`${KEY}.projectionUnavailable`),
          }}
          onToggle={toggleAid(setTrend, setProjection)}
          projection={projection}
          projectionAvailable={view.projectionAvailable}
          trend={trend}
          trendAvailable={view.trendAvailable}
        />
      </motion.section>

      <motion.section
        className={`${String(styles.card)} ${String(styles.tableCard)}`}
        {...screenMotion('enter', { reducedMotion, index: 1 })}
      >
        <TwoDatesTable
          title={t(`${KEY}.tableTitle`)}
          caption={short ? '' : view.tableCaption}
          aHead={
            view.markers === null
              ? nameOf('a')
              : t(`${KEY}.columnA`, { date: view.markers[0].dateLabel })
          }
          bHead={
            view.markers === null
              ? nameOf('b')
              : t(`${KEY}.columnB`, { date: view.markers[1].dateLabel })
          }
          rows={view.rows}
          skeletonRows={MEASUREMENT_PARAMETERS.length}
          waiting={gate.waiting}
          skeleton={gate.skeleton}
          reveal={gate.reveal}
          failed={body.failed ? t(`${KEY}.failed`) : null}
        />
      </motion.section>
    </div>
  );
}
